// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// control.ts — what a thumb does, and the pure functions that decide what it meant.
//
// THE HIT TEST IS TILE-RESOLUTION, NOT A 44px DISC. The obvious scheme — "select the unit within
// 44px of the touch" — collapses exactly where taps matter: at 30px tiles a 44px radius is a 3×3
// tile disc, so one tap at the contact line covers two or three of your own units and the choice
// between them is arbitrary. Instead a tap resolves to the TILE under the touch, then to the unit
// whose centre is nearest within 0.6 of a tile. Tiles are the game's own resolution, they never
// overlap, and the player can see exactly which one they hit.
//
// SELECTION DEFAULTS TO ONE UNIT. Tapping to select the whole connected cluster sounds friendlier
// and is a trap: separation keeps bodies about 0.6 tiles apart, so a fighting army is one
// transitive cluster and a tap would select all twelve — the next ground tap then walks your gate
// Anvils out of the gate. So: tap selects one, a 350ms press expands to the cluster, and a
// double-tap selects every unit of that type. Three deliberate widths, in increasing order of
// commitment.

import { COLS, HALF, ROWS, SUB, chebyshev, colOf, idx, rowOf } from './map';
import { Cmd, tileOf, type State } from './sim';
import { UNITS } from './units';
import { unproject, type View } from './render';

/** Promote press → drag past this, in CSS pixels. Below it, the gesture is still a tap. */
export const DRAG_SLOP = 18;
/** Hold this long on a unit to widen the selection from the unit to its cluster. */
export const PRESS_MS = 350;
/** Two taps closer together than this, on the same unit, mean "every unit of that type". */
export const DOUBLE_MS = 250;
export const DOUBLE_PX = 20;
/** How near a unit's centre a tap has to land, in tiles, to have hit it. */
const PICK_TILES = 6;

/** The unit under a point, or −1. Nearest centre wins, ties by id so it is never arbitrary. */
export function unitAt(s: State, v: View, px: number, py: number, seat: number | null): number {
  const t = unproject(v, px, py);
  if (t < 0) return -1;
  let best = -1;
  let bestD = 1 << 30;
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    if (seat !== null && s.seat[e] !== seat) continue;
    const et = tileOf(s, e);
    if (chebyshev(et, t) > 1) continue;
    // Distance in tenths of a tile, so the threshold reads in the same units the design uses.
    const dx = ((s.x[e] - (colOf(t) * SUB + HALF)) * 10) / SUB;
    const dy = ((s.y[e] - (rowOf(t) * SUB + HALF)) * 10) / SUB;
    const d = dx * dx + dy * dy;
    if (d > PICK_TILES * PICK_TILES) continue;
    if (d < bestD || (d === bestD && e < best)) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

/** Your units whose centres fall inside a screen rectangle. */
export function unitsIn(
  s: State,
  seat: number,
  r: { x0: number; y0: number; x1: number; y1: number },
  project: (x: number, y: number) => [number, number],
): number[] {
  const x0 = Math.min(r.x0, r.x1);
  const x1 = Math.max(r.x0, r.x1);
  const y0 = Math.min(r.y0, r.y1);
  const y1 = Math.max(r.y0, r.y1);
  const out: number[] = [];
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e] || s.seat[e] !== seat) continue;
    const [px, py] = project(s.x[e], s.y[e]);
    if (px >= x0 && px <= x1 && py >= y0 && py <= y1) out.push(e);
  }
  return out;
}

/**
 * The connected group `e` belongs to: your units linked through neighbours within 2 tiles. Used by
 * the press gesture, never by a plain tap.
 */
export function clusterOf(s: State, e: number): number[] {
  const seat = s.seat[e];
  const group = [e];
  const seen = new Uint8Array(s.nextId);
  seen[e] = 1;
  for (let i = 0; i < group.length; i++) {
    const a = group[i];
    for (let b = 0; b < s.nextId; b++) {
      if (seen[b] || !s.alive[b] || s.seat[b] !== seat) continue;
      if (chebyshev(tileOf(s, a), tileOf(s, b)) > 2) continue;
      seen[b] = 1;
      group.push(b);
    }
  }
  return group.sort((a, b) => a - b);
}

export const sameTypeAs = (s: State, e: number): number[] => {
  const out: number[] = [];
  for (let f = 0; f < s.nextId; f++) if (s.alive[f] && s.seat[f] === s.seat[e] && s.type[f] === s.type[e]) out.push(f);
  return out;
};

export interface Order {
  kind: Cmd;
  unit: number;
  arg: number;
}

/**
 * What a tap on the board means, given a selection.
 *
 * ANY board tap that does not land on an enemy is a MOVE. The obvious rule — "tap EMPTY ground to
 * move" — stops working exactly when the game gets interesting: late on, the fighting half of the
 * board is wall-to-wall units and wrecks, and there is no empty ground left to tap. Making every
 * non-enemy tap a move means the primitive never runs out.
 */
export function orderFor(s: State, v: View, px: number, py: number, seat: number, selection: Iterable<number>): Order[] {
  const tile = unproject(v, px, py);
  if (tile < 0) return [];
  const enemy = unitAt(s, v, px, py, 1 - seat);
  const out: Order[] = [];
  for (const e of selection) {
    if (!s.alive[e] || s.seat[e] !== seat) continue;
    if (enemy >= 0) out.push({ kind: Cmd.Attack, unit: e, arg: enemy });
    else out.push({ kind: Cmd.Move, unit: e, arg: tile });
  }
  return out;
}

/**
 * Spread a group's destinations over the tiles around the tap, so eleven units ordered to one tile
 * do not all try to stand on it and shove each other back out. Deterministic: the offsets are a
 * fixed ring walked in a fixed order.
 */
const RING: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
  [2, 0],
  [-2, 0],
  [0, 2],
  [0, -2],
  [2, 1],
  [-2, 1],
  [2, -1],
  [-2, -1],
];

export function spread(s: State, tile: number, units: readonly number[]): number[] {
  const c0 = colOf(tile);
  const r0 = rowOf(tile);
  const out: number[] = [];
  let k = 0;
  for (let i = 0; i < units.length; i++) {
    let t = tile;
    while (k < RING.length) {
      const c = c0 + RING[k][0];
      const r = r0 + RING[k][1];
      k++;
      if (c < 0 || c >= COLS || r < 0 || r >= ROWS) continue;
      const cand = idx(c, r);
      if (s.map.terrain[cand] === 1) continue;
      t = cand;
      break;
    }
    out.push(t);
  }
  return out;
}

/** The build button's live state. Kept pure so the tutorial and the tests can ask the same question. */
export function buildState(s: State, seat: number, type: number): { can: boolean; why: string } {
  const side = s.sides[seat];
  const spec = UNITS[type];
  if (s.mode.buildBudget > 0 && side.presses >= s.mode.buildBudget) return { can: false, why: 'No presses left' };
  if (side.queue.length >= 2) return { can: false, why: 'Queue full' };
  let alive = 0;
  for (let e = 0; e < s.nextId; e++) if (s.alive[e] && s.seat[e] === seat) alive++;
  if (alive + side.queue.length >= s.mode.unitCap) return { can: false, why: 'At the cap' };
  if (side.reserve < spec.cost) return { can: false, why: `Needs ${spec.cost}` };
  return { can: true, why: '' };
}
